import { getModel } from '../../model/registry'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import generateEndpoint from '../../utils/generateEndpoint'
import useApi from '../../hooks/useApi'
import useStaleTime from '../../hooks/useStaleTime'

import CardView from './CardView'
import Loader from '../generic/Loader'
import { FormStateProvider, MessageProvider, useStore } from '../pageView/Page'
import { ModalProvider } from '../modal/useModalDialog'

// Initializes the child store from fetched data, then renders CardView.
// Lives inside its own FormStateProvider so useStore reads the child's store.
const CardEmbedInit = ({ config, record, mode, type, dataUpdatedAt }) => {
    const [state, setState] = useStore(s => s)
    const lastProcessedRef = useRef(null)

    useEffect(() => {
        if (dataUpdatedAt === lastProcessedRef.current) return
        lastProcessedRef.current = dataUpdatedAt

        if (mode === 'edit' && record) {
            setState({ ...record, loaded: true, entity: config.entity })
        } else {
            setState({ loaded: true, entity: config.entity })
        }
    }, [dataUpdatedAt, mode])

    if (!state.loaded) return <Loader />

    const options = mode === 'edit'
        ? { mode: 'edit', load: true }
        : { mode: 'new', load: false }

    return <CardView type={type} options={options} />
}

const CardEmbed = ({ type }) => {
    const config = getModel('cardViewData')[type]
    const { id } = useParams()
    const endpoint = generateEndpoint(config.path, id)
    const { getMe } = useApi()
    const staleTime = useStaleTime(config.entity)

    const { data, isLoading, isError, isSuccess, dataUpdatedAt } = useQuery({
        queryKey: [endpoint],
        queryFn: () => getMe(endpoint),
        staleTime,
    })

    if (isLoading) return <Loader />
    if (isError) return <span>Error loading {config.entity}</span>

    const responseData = isSuccess ? data?.data : null
    const record = Array.isArray(responseData)
        ? (responseData.length > 0 ? responseData[0] : null)
        : responseData
    const mode = record?.id != null ? 'edit' : 'new'

    return (
        <FormStateProvider>
            <MessageProvider>
                <ModalProvider>
                    <CardEmbedInit
                        config={config}
                        record={record}
                        mode={mode}
                        type={type}
                        dataUpdatedAt={dataUpdatedAt}
                    />
                </ModalProvider>
            </MessageProvider>
        </FormStateProvider>
    )
}

export default CardEmbed
